(() => {
  if (customElements.get('blog-insights-slider')) return;

  const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
  const DURATION = 600;

  class BlogInsightsSlider extends HTMLElement {
    connectedCallback() {
      this.offset = 0;
      this.goal = 0;
      this.running = false;
      this.dragging = false;
      this.didDrag = false;
      this.startX = 0;
      this.ignoreResize = false;
      this.mq = window.matchMedia('(max-width: 749px)');
      this.bind();
      this.layout();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.mq?.removeEventListener('change', this.onBreakpoint);
      this.ro?.disconnect();
      if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
      this.clearContentAlign();
    }

    get track() {
      return this.querySelector('[data-kv-track]');
    }

    bind() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;

      this.onBreakpoint = () => {
        this.offset = 0;
        this.goal = 0;
        this.layout();
      };
      this.mq.addEventListener('change', this.onBreakpoint, { signal });

      this.onWindowResize = () => {
        if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
        this.resizeRaf = requestAnimationFrame(() => {
          this.resizeRaf = 0;
          if (this.running || this.ignoreResize) {
            this.needsLayout = true;
            return;
          }
          this.layout();
        });
      };
      window.addEventListener('resize', this.onWindowResize, { signal });

      if ('ResizeObserver' in window) {
        this.ro?.disconnect();
        this.ro = new ResizeObserver(() => {
          if (this.ignoreResize || this.running) {
            this.needsLayout = true;
            return;
          }
          this.layout();
        });
        this.ro.observe(this);
        if (this.track) this.ro.observe(this.track);
      }

      this.addEventListener('pointerdown', this.onPointerDown, { signal });
      this.addEventListener('pointermove', this.onPointerMove, { signal });
      this.addEventListener('pointerup', this.onPointerUp, { signal });
      this.addEventListener('pointercancel', this.onPointerUp, { signal });
      this.addEventListener('click', this.onClick, { signal });
      this.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    }

    onPointerDown(event) {
      if (!this.canSlide()) return;
      if (event.button && event.button !== 0) return;
      if (!event.target.closest('[data-kv-track]')) return;
      this.dragging = true;
      this.didDrag = false;
      this.startX = event.clientX;
      this.setPointerCapture?.(event.pointerId);
    }

    onPointerMove(event) {
      if (!this.dragging) return;
      if (Math.abs(event.clientX - this.startX) > 6) this.didDrag = true;
    }

    onPointerUp(event) {
      if (!this.dragging) return;
      this.dragging = false;
      const dx = event.clientX - this.startX;
      if (Math.abs(dx) < 30) return;
      this.shift(dx < 0 ? 1 : -1);
    }

    onClick(event) {
      if (this.didDrag) {
        event.preventDefault();
        event.stopPropagation();
      }
    }

    canSlide() {
      return !this.mq.matches && this.visibleSlides().length >= 5;
    }

    visibleSlides() {
      const mobile = this.mq.matches;
      const slides = Array.from(this.querySelectorAll('[data-kv-slide]')).filter((slide) => {
        return mobile ? slide.dataset.showMobile === 'true' : slide.dataset.showDesktop === 'true';
      });
      return mobile ? slides.slice(0, 3) : slides;
    }

    gap() {
      if (!this.track) return 8;
      if (this.mq.matches) return 8;
      return Math.max(8, this.track.clientWidth * (18.657 / 1250));
    }

    sizes(count) {
      if (this.mq.matches) {
        const mobile = [
          { grow: 1, h: 132, r: 4.74 },
          { grow: 1, h: 159, r: 4.74 },
          { grow: 1, h: 100, r: 4.74 },
        ];
        return mobile.slice(0, Math.max(1, count));
      }

      const trackWidth = this.track ? this.track.clientWidth : 1250;
      const scale = trackWidth / 1250;
      const radius = 14.925 * scale;
      const three = [
        { grow: 404.851, h: 414.179 * scale, r: radius },
        { grow: 403.918, h: 500 * scale, r: radius },
        { grow: 404.851, h: 316.231 * scale, r: radius },
      ];

      if (count === 3) return three;
      if (count === 2) return [three[0], three[1]];
      if (count === 1) return [three[1]];
      if (count === 4) {
        return [
          { grow: 302, h: 360 * scale, r: radius },
          { grow: 302, h: 414 * scale, r: radius },
          { grow: 302, h: 500 * scale, r: radius },
          { grow: 302, h: 316 * scale, r: radius },
        ];
      }

      return [
        { grow: 230, h: 280 * scale, r: radius },
        { grow: 248, h: 414 * scale, r: radius },
        { grow: 248, h: 500 * scale, r: radius },
        { grow: 248, h: 316 * scale, r: radius },
        { grow: 230, h: 260 * scale, r: radius },
      ];
    }

    peakSlot(count) {
      if (this.mq.matches || count === 3) return Math.min(1, count - 1);
      if (count >= 4) return 2;
      return 0;
    }

    wrap(value, count) {
      return ((value % count) + count) % count;
    }

    slotRects(count) {
      const sizes = this.sizes(count);
      const gap = this.gap();
      const width = this.track ? this.track.clientWidth : 0;
      const totalGrow = sizes.reduce((sum, item) => sum + item.grow, 0);
      const available = Math.max(0, width - gap * Math.max(0, count - 1));
      let left = 0;
      return sizes.map((size) => {
        const rect = {
          left,
          width: totalGrow ? (available * size.grow) / totalGrow : 0,
          height: size.h,
          radius: size.r,
        };
        left += rect.width + gap;
        return rect;
      });
    }

    syncCrumbInset() {
      const root = this.closest('.shopify-section');
      if (!root) return;
      root.style.setProperty('--bih-crumb-inset', '0px');
    }

    syncStageWidth() {
      const root = this.closest('.shopify-section');
      if (!root) return;
      root.style.removeProperty('--bih-stage-max');
      document.documentElement.style.removeProperty('--bih-stage-max');
    }

    clearContentAlign() {
      const doc = document.documentElement;
      doc.style.removeProperty('--blog-insights-align-left');
      doc.style.removeProperty('--blog-insights-align-right');
      doc.style.removeProperty('--blog-insights-align-width');
    }

    syncContentAlign() {
      this.clearContentAlign();
    }

    apply(slide, rect) {
      slide.style.transform = `translate3d(${rect.left}px, 0, 0)`;
      slide.style.width = `${rect.width}px`;
      slide.style.height = `${rect.height}px`;
      slide.style.borderRadius = `${rect.radius}px`;
    }

    move(slide, from, to) {
      this.apply(slide, from);
      slide.getBoundingClientRect();

      if (typeof slide.animate !== 'function') {
        this.apply(slide, to);
        return Promise.resolve();
      }

      const animation = slide.animate(
        [
          {
            transform: `translate3d(${from.left}px, 0, 0)`,
            width: `${from.width}px`,
            height: `${from.height}px`,
            borderRadius: `${from.radius}px`,
          },
          {
            transform: `translate3d(${to.left}px, 0, 0)`,
            width: `${to.width}px`,
            height: `${to.height}px`,
            borderRadius: `${to.radius}px`,
          },
        ],
        { duration: DURATION, easing: EASE, fill: 'forwards' }
      );

      const finished = animation.finished ? animation.finished.catch(() => {}) : Promise.resolve();
      const timeout = new Promise((resolve) => window.setTimeout(resolve, DURATION + 40));

      return Promise.race([finished, timeout]).then(() => {
        try {
          animation.cancel();
        } catch (error) {
          /* ignore */
        }
        this.apply(slide, to);
      });
    }

    syncMeta(slides, count) {
      const peak = this.peakSlot(count);
      slides.forEach((slide, index) => {
        const slot = this.wrap(index - this.offset, count);
        slide.dataset.slot = String(slot);
        slide.toggleAttribute('data-peak', slot === peak);
        slide.style.zIndex = slot === peak ? '2' : '1';
      });
    }

    layout() {
      const slides = this.visibleSlides();
      const count = slides.length;
      if (!count || !this.track) return;
      this.offset = this.wrap(this.offset, count);
      this.goal = this.wrap(this.goal, count);
      this.syncStageWidth();
      this.syncContentAlign();
      this.syncCrumbInset();
      const rects = this.slotRects(count);
      this.track.style.height = `${Math.max(...rects.map((rect) => rect.height))}px`;
      this.dataset.kvMode = this.canSlide() ? 'slide' : 'static';
      this.dataset.kvCount = String(count);

      this.querySelectorAll('[data-kv-slide]').forEach((slide) => {
        if (slides.includes(slide)) return;
        slide.dataset.slot = 'off';
        slide.removeAttribute('data-peak');
      });

      slides.forEach((slide, index) => {
        this.apply(slide, rects[this.wrap(index - this.offset, count)]);
      });
      this.syncMeta(slides, count);
    }

    shift(step) {
      if (!this.canSlide()) return;
      const count = this.visibleSlides().length;
      if (count < 5) return;
      this.goal = this.wrap(this.goal + step, count);
      this.runGoal();
    }

    runGoal() {
      if (this.running) return;
      this.running = true;
      this.ignoreResize = true;

      const safety = window.setTimeout(() => {
        this.running = false;
        this.ignoreResize = false;
        if (this.needsLayout) {
          this.needsLayout = false;
          this.layout();
        }
      }, 4000);

      const finish = () => {
        window.clearTimeout(safety);
        this.running = false;
        this.ignoreResize = false;
        if (this.needsLayout) {
          this.needsLayout = false;
          this.layout();
        }
      };

      const tick = () => {
        const count = this.visibleSlides().length;
        if (count < 2) {
          finish();
          return;
        }
        const target = this.wrap(this.goal, count);
        if (target === this.offset) {
          finish();
          return;
        }
        let delta = this.wrap(target - this.offset, count);
        if (delta > count / 2) delta -= count;
        this.animateStep(delta > 0 ? 1 : -1)
          .catch(() => {})
          .then(tick);
      };

      tick();
    }

    animateStep(step) {
      const slides = this.visibleSlides();
      const count = slides.length;
      if (count < 2 || !this.track) return Promise.resolve();

      const fromOffset = this.offset;
      const toOffset = this.wrap(fromOffset + step, count);
      const rects = this.slotRects(count);
      const gap = this.gap();
      const trackWidth = this.track.clientWidth;
      const jobs = [];

      slides.forEach((slide, index) => {
        const fromSlot = this.wrap(index - fromOffset, count);
        const toSlot = this.wrap(index - toOffset, count);
        const from = rects[fromSlot];
        const to = rects[toSlot];
        if (!from || !to) return;

        if (step > 0 && fromSlot === 0 && toSlot === count - 1) {
          const exit = { ...from, left: -from.width - gap };
          const enterFrom = { ...to, left: trackWidth + gap };
          slide.style.zIndex = '0';
          const clone = this.spawnClone(slide, enterFrom);
          jobs.push(this.move(slide, from, exit).then(() => this.apply(slide, to)));
          jobs.push(this.move(clone, enterFrom, to).then(() => clone.remove()));
          return;
        }

        if (step < 0 && fromSlot === count - 1 && toSlot === 0) {
          const exit = { ...from, left: trackWidth + gap };
          const enterFrom = { ...to, left: -to.width - gap };
          slide.style.zIndex = '0';
          const clone = this.spawnClone(slide, enterFrom);
          jobs.push(this.move(slide, from, exit).then(() => this.apply(slide, to)));
          jobs.push(this.move(clone, enterFrom, to).then(() => clone.remove()));
          return;
        }

        jobs.push(this.move(slide, from, to));
      });

      this.offset = toOffset;
      this.syncMeta(slides, count);
      return Promise.all(jobs);
    }

    spawnClone(slide, rect) {
      const clone = slide.cloneNode(true);
      clone.classList.add('is-clone');
      clone.removeAttribute('data-kv-slide');
      this.apply(clone, rect);
      this.track.appendChild(clone);
      return clone;
    }
  }

  customElements.define('blog-insights-slider', BlogInsightsSlider);
})();
